import { useEffect, useState } from 'react';
import type { DayReport } from '@360face/domain';
import { ApiError } from '../api/client';
import type { StaffApi } from './api';
import { tabletDay, tabletMoney, type TabletLocale, type TabletMessages } from './messages';

/** Discounts (with reasons, Altegio status) and tips per therapist for the selected day. */
export function DayReportView({
  api,
  date,
  staffName,
  t,
  locale,
  onBack,
}: {
  api: StaffApi;
  date?: string;
  staffName: (id: string) => string;
  t: TabletMessages;
  locale: TabletLocale;
  onBack: () => void;
}) {
  const [r, setR] = useState<DayReport>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    api
      .dayReport(date)
      .then(setR, (e) => setError(t.errors[e instanceof ApiError ? e.code : 'default'] ?? t.errors.default));
  }, [api, date, t]);
  const money = (amountMinor: number) => tabletMoney({ amountMinor, currency: r?.currency ?? 'EUR' }, locale);
  const time = (iso: string) =>
    new Date(iso).toLocaleTimeString(locale === 'en' ? 'en-GB' : locale === 'ru' ? 'ru-RU' : 'pt-PT', {
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <div className="tablet detail">
      <header className="t-bar">
        <button className="t-ghost" onClick={onBack}>
          {t.back}
        </button>
        <strong>{t.reportTitle(r ? tabletDay(r.date, locale) : '…')}</strong>
      </header>
      {error && (
        <p className="t-warn" role="alert">
          {error}
        </p>
      )}
      {r && !r.discounts.length && !r.upgrades.length && !r.tips.length && (
        <p className="t-info r-empty">{t.reportNone}</p>
      )}
      {r && r.discounts.length > 0 && (
        <section className="t-card">
          <h3>
            {t.reportDiscounts}: {r.discounts.length} · −{money(r.totals.discountMinor)}
          </h3>
          <table className="c-stock">
            <tbody>
              {r.discounts.map((d) => (
                <tr key={d.visitId}>
                  <td>{time(d.at)}</td>
                  <td>{staffName(d.staffId)}</td>
                  <td>
                    {d.clientName}
                    <br />
                    <small>{d.services.join(', ')}</small>
                  </td>
                  <td>
                    {money(d.beforeMinor)} − {money(d.amountMinor)} = <strong>{money(d.afterMinor)}</strong>
                  </td>
                  <td>
                    {t.reasons[d.reason]}
                    {d.note && (
                      <>
                        <br />
                        <small>{d.note}</small>
                      </>
                    )}
                  </td>
                  <td className={d.crm === 'ok' ? '' : 'r-warn'}>{t.discountCrm[d.crm]}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="t-hint">
            {t.reportByReason}:{' '}
            {r.totals.byReason.map((b) => `${t.reasons[b.reason]} ${b.count}× −${money(b.amountMinor)}`).join(' · ')}
          </p>
        </section>
      )}
      {r && r.upgrades.length > 0 && (
        <section className="t-card">
          <h3>{t.reportUpgrades}</h3>
          <table className="c-stock">
            <tbody>
              {r.upgrades.map((u) => (
                <tr key={u.visitId}>
                  <td>{time(u.at)}</td>
                  <td>{staffName(u.staffId)}</td>
                  <td>
                    {u.clientName}
                    <br />
                    <small>{u.services.join(', ')}</small>
                  </td>
                  <td>
                    {money(u.beforeMinor)} → <strong>{money(u.finalMinor)}</strong>
                  </td>
                  <td className={u.crm === 'ok' ? '' : 'r-warn'}>{t.discountCrm[u.crm]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {r && r.tips.length > 0 && (
        <section className="t-card">
          <h3>{t.reportTips}</h3>
          <table className="c-stock">
            <tbody>
              {r.totals.tipsByStaff.map((s) => (
                <tr key={s.staffId}>
                  <td>{staffName(s.staffId)}</td>
                  <td>
                    <strong>{money(s.amountMinor)}</strong> ({s.count})
                  </td>
                  <td>
                    {r.tips
                      .filter((x) => x.staffId === s.staffId)
                      .map((x) => `${time(x.at)} ${money(x.amountMinor)} ${t.methods[x.method]}`)
                      .join(' · ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
